import { useState, useEffect, useCallback, useRef } from 'react';
import { useActivityTracker } from './useActivityTracker';
import { onExtendSessionReceived, broadcastExtendSession } from '@/utils/broadcastChannel';
import { authApi } from '@/services/auth';

/**
 * Interval for checking timeout status (1 second).
 */
const CHECK_INTERVAL_MS = 1000;

/**
 * Threshold for sending keep-alive refresh when user is active
 * but no API call has been made (5 minutes).
 */
const KEEP_ALIVE_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Threshold to detect computer sleep/wake.
 * If interval fires after more than this time, computer likely slept.
 */
const SLEEP_DETECTION_THRESHOLD_MS = 2000;

/**
 * Options for the session timeout hook.
 */
export interface UseSessionTimeoutOptions {
  /**
   * Total timeout duration in milliseconds.
   * After this period of inactivity, onTimeout will be called.
   */
  timeoutMs: number;
  /**
   * Warning threshold in milliseconds.
   * When remaining time is less than this, showWarning becomes true.
   */
  warningMs: number;
  /**
   * Callback to execute when session times out.
   */
  onTimeout: () => void;
  /**
   * Whether timeout tracking is enabled.
   * Should be false when user is not authenticated.
   */
  enabled: boolean;
}

/**
 * Return type for the session timeout hook.
 */
export interface UseSessionTimeoutResult {
  /**
   * Whether the warning modal should be shown.
   */
  showWarning: boolean;
  /**
   * Remaining time until timeout in milliseconds.
   */
  remainingMs: number;
  /**
   * Function to extend the session (reset timeout).
   * Called when user clicks "Stay logged in".
   */
  extendSession: () => void;
  /**
   * Function to record an API call.
   * Should be called from API interceptor to track API activity.
   */
  recordApiCall: () => void;
}

/**
 * Hook for managing session timeout with warning and keep-alive functionality.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * Features:
 * - Tracks user activity via useActivityTracker
 * - Shows warning before timeout
 * - Handles keep-alive (token refresh) when user is active but no API calls
 * - Detects computer sleep/wake and handles appropriately
 * - Syncs with other tabs via BroadcastChannel
 *
 * @param options - Configuration options
 * @returns Session timeout state and control functions
 */
export function useSessionTimeout({
  timeoutMs,
  warningMs,
  onTimeout,
  enabled,
}: UseSessionTimeoutOptions): UseSessionTimeoutResult {
  const { lastActivity, updateActivity } = useActivityTracker({ enabled });
  const [showWarning, setShowWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(timeoutMs);
  const lastApiCallRef = useRef(Date.now());
  const lastCheckRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);
  const hasTimedOutRef = useRef(false);

  /**
   * Record an API call to track API activity.
   * This helps determine when to send keep-alive.
   */
  const recordApiCall = useCallback(() => {
    lastApiCallRef.current = Date.now();
  }, []);

  /**
   * Extend the session by updating activity and refreshing token.
   * Called when user clicks "Stay logged in" or interacts with warning.
   */
  const extendSession = useCallback(() => {
    // Update local activity
    updateActivity();
    setShowWarning(false);

    // Broadcast to other tabs
    broadcastExtendSession();

    // Trigger a refresh to ensure tokens are fresh
    authApi.refresh().catch(() => {
      // Refresh failed, but we've already reset the local timer
      // Session will expire naturally if refresh keeps failing
    });
  }, [updateActivity]);

  // Main timeout check interval
  useEffect(() => {
    if (!enabled) {
      // Reset state when disabled
      setShowWarning(false);
      setRemainingMs(timeoutMs);
      hasTimedOutRef.current = false;
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastActivity;
      const remaining = timeoutMs - elapsed;
      const sinceLastApiCall = now - lastApiCallRef.current;
      const sinceLastCheck = now - lastCheckRef.current;

      // Update last check time
      lastCheckRef.current = now;

      // Sleep/wake detection: if more than 2 seconds passed since last check,
      // the computer likely slept
      if (sinceLastCheck > SLEEP_DETECTION_THRESHOLD_MS) {
        // Check if session should have expired during sleep
        if (remaining <= 0 && !hasTimedOutRef.current) {
          hasTimedOutRef.current = true;
          onTimeout();
          return;
        }
      }

      // Update remaining time
      setRemainingMs(Math.max(0, remaining));

      // Timeout check
      if (remaining <= 0 && !hasTimedOutRef.current) {
        hasTimedOutRef.current = true;
        onTimeout();
        return;
      }

      // Warning check
      if (remaining <= warningMs && remaining > 0) {
        if (!showWarning) {
          setShowWarning(true);
        }
      } else if (remaining > warningMs && showWarning) {
        // User became active, dismiss warning
        setShowWarning(false);
      }

      // Keep-alive check: If user is active but no API call recently
      // Only send keep-alive if not in warning zone and not already refreshing
      if (
        remaining > warningMs &&
        sinceLastApiCall > KEEP_ALIVE_THRESHOLD_MS &&
        !isRefreshingRef.current
      ) {
        isRefreshingRef.current = true;
        authApi
          .refresh()
          .then(() => {
            lastApiCallRef.current = Date.now();
          })
          .catch(() => {
            // Refresh failed - don't retry, session will expire naturally
            // This is expected security behavior
          })
          .finally(() => {
            isRefreshingRef.current = false;
          });
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled, lastActivity, timeoutMs, warningMs, onTimeout, showWarning]);

  // Listen for extend session broadcasts from other tabs
  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = onExtendSessionReceived(() => {
      // Other tab extended the session, dismiss warning
      setShowWarning(false);
      // Note: lastActivity will be updated via activity broadcast listener
    });

    return unsubscribe;
  }, [enabled]);

  // Reset hasTimedOut when enabled changes or timeout changes
  useEffect(() => {
    hasTimedOutRef.current = false;
  }, [enabled, timeoutMs]);

  return {
    showWarning,
    remainingMs,
    extendSession,
    recordApiCall,
  };
}

export default useSessionTimeout;
